import type { ConversationChannel } from "@repo/database";
import { z } from "zod";

/** What a person on the team can write to the shopper in one message. */
export const STAFF_MESSAGE_MAX = 4000;

/**
 * Per-conversation events for the shopper's page (ADR-9), on Redis `conv:<id>:events`: staff
 * replies and the end of a handoff. They carry the message itself, so the page appends it
 * without a refetch.
 */
export type ConversationEvent =
	| {
			type: "message";
			message: {
				id: string;
				role: "staff";
				text: string;
				authorName: string | null;
				createdAt: string;
			};
	  }
	/** The team handed the conversation back to the agent, or resolved it. */
	| { type: "handoff_released" };

/**
 * What `GET /api/public/:slug/conversations/:id/events` streams: every (re)connect opens with
 * `ready` and the current handoff state, then the conversation's events.
 */
export type ConversationStreamEvent =
	| { type: "ready"; requested: boolean; paused: boolean }
	| ConversationEvent;

/** What an adapter needs to reach the shopper of a conversation (ADR-9). */
export interface ConversationRef {
	id: string;
	organizationId: string;
	channel: ConversationChannel;
	storeId: string;
	externalShopperId: string | null;
	lastInboundAt: Date | null;
	/** The WhatsApp number it talks on; read from the row when the caller did not load it. */
	connectionId?: string | null;
}

export interface OutboundMessage {
	/** The stored ConversationMessage id; the WEB page and API webhooks key by it. */
	id: string;
	text: string;
	author: { kind: "staff" | "agent" | "system"; name: string | null };
	createdAt: Date;
}

/**
 * `unknown`: the channel did not answer (a timeout, a network error), so the message may have
 * reached the shopper. The others are definite refusals: it did not.
 */
export type SendResult =
	| { ok: true }
	| {
			ok: false;
			reason: "window_closed" | "not_connected" | "failed" | "unknown";
			detail?: string | undefined;
	  };

/**
 * Appended to a saved message that may not have reached the shopper: `not_sent`, the agent's
 * WhatsApp reply held back because a person took over while it was being written, and
 * `unknown`, a staff reply the channel did not confirm. A `data-*` part, so the model never
 * reads it (convertToModelMessages drops data parts); the inbox shows it.
 */
export const DELIVERY_PART = "data-delivery";
export type Delivery = "not_sent" | "unknown";
export interface DeliveryPart {
	type: typeof DELIVERY_PART;
	data: { state: Delivery };
}

export const deliveryPart = (state: Delivery) =>
	({ type: DELIVERY_PART, data: { state } }) satisfies DeliveryPart;

/** What a saved message's delivery marker says, or null when it has none. */
export function deliveryOf(parts: unknown): Delivery | null {
	if (!Array.isArray(parts)) {
		return null;
	}
	const marker = parts.find(
		(part): part is DeliveryPart =>
			(part as DeliveryPart | null)?.type === DELIVERY_PART,
	);
	return marker?.data.state ?? null;
}

/**
 * Delivers a non-streamed message (a staff reply) to the shopper on a conversation's channel.
 * Streamed agent turns never pass through here.
 */
export interface ChannelAdapter {
	channel: ConversationChannel;
	send(
		conversation: ConversationRef,
		message: OutboundMessage,
	): Promise<SendResult>;
}

/** Shapes the Channels screen saves; the API re-checks every field. */
export const whatsappConnectionSchema = z.object({
	phoneNumberId: z
		.string()
		.trim()
		.regex(
			/^\d{6,20}$/,
			"Phone number ID is the long number Meta shows, digits only",
		),
	wabaId: z
		.string()
		.trim()
		.regex(/^\d{6,20}$/, "WhatsApp Business Account ID is digits only"),
	displayPhone: z
		.string()
		.trim()
		.regex(
			/^\+?[\d ()-]{6,24}$/,
			"Enter the number as shoppers see it, e.g. +961 70 123 456",
		),
	/** Store.externalId conversations open in; null = the agent's default store. */
	storeExternalId: z.string().trim().min(1).nullable(),
});

/** A WhatsApp ChannelConnection's `config`; the number itself is the row's externalId. */
export type WhatsappConnectionConfig = Omit<
	z.infer<typeof whatsappConnectionSchema>,
	"phoneNumberId"
>;

export const accessTokenSchema = z
	.string()
	.trim()
	.min(20, "Paste the whole access token from Meta")
	.max(1024);

export const testRecipientSchema = z
	.string()
	.trim()
	.transform((value) => value.replace(/[\s()-]/g, "").replace(/^\+/, ""))
	.pipe(
		z
			.string()
			.regex(/^\d{8,15}$/, "Enter a phone number with its country code"),
	);

/** The template every WhatsApp Business account starts with; works outside the 24 h window. */
export const TEST_TEMPLATE = {
	name: "hello_world",
	language: "en_US",
} as const;

/* ------------------------------------------------------------------------------------------
 * WhatsApp commerce (spec 3.17): the shapes of every channels procedure, so the admin is built
 * against them while the procedures are written. Bodies belong to the owners named there.
 * ---------------------------------------------------------------------------------------- */

/**
 * Meta's limits these forms check, as in `WHATSAPP_LIMITS` and `CALLING_LIMITS` of
 * @repo/whatsapp. Repeated here because this file is imported by the browser, and that
 * package is not (it signs with node:crypto).
 */
const META_LIMITS = {
	iceBreakers: 4,
	iceBreakerChars: 80,
	commands: 30,
	commandName: 32,
	commandHint: 256,
	voicemailTimeoutMaxSeconds: 30,
} as const;

const LINK_CODE_RE = /^[A-Z2-7]{4,8}$/;

/**
 * A wa.me link (and its QR) for the retailer or one branch (plan 7.1). `prefill: false` keeps
 * the ice breakers visible; `prefill: true` starts the chat with `text`, which ends in the
 * exact token `[<code>]` the inbound classifier strips and records as the entry.
 */
export const entryLinkSchema = z
	.object({
		/** 4-8 base32 characters, unique per number. */
		code: z
			.string()
			.trim()
			.toUpperCase()
			.regex(LINK_CODE_RE, "Use 4 to 8 letters A-Z or digits 2-7"),
		label: z.string().trim().min(1, "Give the link a name").max(60),
		storeExternalId: z.string().trim().min(1).optional(),
		prefill: z.boolean(),
		text: z.string().trim().max(500).optional(),
	})
	.refine((link) => !link.prefill || link.text?.endsWith(`[${link.code}]`), {
		message: "A pre-filled message ends with the link's code in brackets",
		path: ["text"],
	});
export type EntryLink = z.infer<typeof entryLinkSchema>;

/** Ice breakers and slash commands a number shows (no emojis; Meta's limits). */
export const automationSchema = z.object({
	iceBreakers: z
		.array(z.string().trim().min(1).max(META_LIMITS.iceBreakerChars))
		.max(META_LIMITS.iceBreakers),
	commands: z
		.array(
			z.object({
				name: z
					.string()
					.trim()
					.regex(/^[a-z0-9_]+$/, "Lowercase letters, digits and _")
					.max(META_LIMITS.commandName),
				hint: z.string().trim().min(1).max(META_LIMITS.commandHint),
			}),
		)
		.max(META_LIMITS.commands),
});
export type Automation = z.infer<typeof automationSchema>;

const hhmm = z.string().regex(/^([01]\d|2[0-3])[0-5]\d$/, "A time as HHMM");
const DAYS = [
	"MONDAY",
	"TUESDAY",
	"WEDNESDAY",
	"THURSDAY",
	"FRIDAY",
	"SATURDAY",
	"SUNDAY",
] as const;

/**
 * A number's calling settings (spec 2.7). `buttonCountries` matches the country a caller's
 * WhatsApp number is registered in, not where they are. Call hours and voicemail exclude each
 * other: Meta refuses calls outside hours, so voicemail would never play.
 */
export const callingConfigSchema = z
	.object({
		enabled: z.boolean(),
		callButton: z.enum(["show", "hide"]),
		buttonCountries: z.array(z.string().regex(/^[A-Z]{2}$/)),
		hours: z.object({
			enabled: z.boolean(),
			timezone: z.string().min(1),
			weekly: z.array(
				z.object({ day: z.enum(DAYS), open: hhmm, close: hhmm }),
			),
			holidays: z
				.array(
					z.object({
						date: z.iso.date(),
						start: hhmm,
						end: hhmm,
					}),
				)
				.max(20),
		}),
		callbackPermission: z.boolean(),
		voicemail: z.object({
			enabled: z.boolean(),
			timeoutSeconds: z
				.number()
				.int()
				.min(0)
				.max(META_LIMITS.voicemailTimeoutMaxSeconds),
			announcementMediaId: z.string().optional(),
		}),
		maxConcurrent: z.number().int().min(1).max(20).default(3),
		maxMinutes: z.number().int().min(1).max(60).default(10),
		/** What Meta reported last; read only. */
		meta: z
			.object({
				status: z.string().nullable(),
				callIcon: z.string().nullable(),
				sip: z.string().nullable(),
				restrictions: z.array(z.string()),
				syncedAt: z.string().nullable(),
				changedOutside: z.boolean().optional(),
			})
			.optional(),
	})
	.refine(
		(calling) => !(calling.hours.enabled && calling.voicemail.enabled),
		{
			message:
				"Turn call hours off while voicemail is on: Meta refuses calls outside hours, so voicemail would never play",
			path: ["hours", "enabled"],
		},
	)
	.refine(
		(calling) =>
			DAYS.every(
				(day) =>
					calling.hours.weekly.filter((slot) => slot.day === day)
						.length <= 2,
			),
		{
			message: "At most two opening slots a day",
			path: ["hours", "weekly"],
		},
	);
export type CallingConfig = z.infer<typeof callingConfigSchema>;

/** The checkout Flow of a number (B3 writes it). */
export interface CheckoutFlowConfig {
	flowId: string;
	status: string;
	jsonHash: string;
	jsonVersion: string;
	publishedAt: string | null;
	/** Meta's signature status of the number's Flow public key at the last setup (VALID, MISMATCH). */
	keySignature?: string | null;
	health: {
		p90Ms: number | null;
		errorRate: number | null;
		alert: string | null;
	};
}

/**
 * `ChannelConnection.config` of a WhatsApp number (spec 2.7). The keys before
 * `defaultStoreExternalId` are the old single-number shape, kept until the next release:
 * readers use the new key first.
 */
export interface WhatsappNumberConfig
	extends Partial<WhatsappConnectionConfig> {
	defaultStoreExternalId?: string | null;
	automation?: Automation;
	calling?: CallingConfig;
	flows?: { checkout?: CheckoutFlowConfig };
	testRecipients?: string[];
	devOverride?: { host: string; setAt: string; by: string } | null;
	entryLinks?: EntryLink[];
}

/** The branch a number's new conversations open in, by the new key first. */
export function defaultStoreOf(config: unknown): string | null {
	const value = (config ?? {}) as WhatsappNumberConfig;
	return value.defaultStoreExternalId ?? value.storeExternalId ?? null;
}

export const organizationInput = z.object({
	organizationId: z.string().min(1),
});
export const numberInput = organizationInput.extend({
	connectionId: z.string().min(1),
});

export const healthBadgeSchema = z.enum([
	"connected",
	"attention",
	"flagged",
	"restricted",
	"disconnected",
	"unknown",
]);

/** One number as the Channels hub lists it. */
export const numberSummarySchema = z.object({
	id: z.string(),
	label: z.string().nullable(),
	phoneNumberId: z.string(),
	displayPhone: z.string().nullable(),
	wabaId: z.string().nullable(),
	whatsappAccountId: z.string().nullable(),
	storeScope: z.array(z.string()),
	defaultStoreExternalId: z.string().nullable(),
	enabled: z.boolean(),
	health: healthBadgeSchema,
	status: z.string().nullable(),
	qualityRating: z.string().nullable(),
	messagingLimit: z.string().nullable(),
	nameStatus: z.string().nullable(),
	callingStatus: z.string().nullable(),
	isOnBizApp: z.boolean(),
	costThisMonthUsd: z.number(),
	freeServiceUsed: z.number().int(),
	openAlerts: z.number().int(),
	lastError: z.string().nullable(),
	checkedAt: z.date().nullable(),
});
export type NumberSummary = z.infer<typeof numberSummarySchema>;

export const accountSummarySchema = z.object({
	id: z.string(),
	wabaId: z.string(),
	waacId: z.string().nullable(),
	name: z.string().nullable(),
	currency: z.string().nullable(),
	businessVerification: z.string().nullable(),
	reviewStatus: z.string().nullable(),
	onboarding: z.string(),
	paymentMethodMissing: z.boolean(),
	tokenExpiresAt: z.date().nullable(),
	numbers: z.number().int(),
});

export const numbersListSchema = z.object({
	numbers: z.array(numberSummarySchema),
	accounts: z.array(accountSummarySchema),
	webhook: z.object({ ready: z.boolean() }),
});

export const businessProfileSchema = z.object({
	about: z.string().trim().max(139).optional(),
	address: z.string().trim().max(256).optional(),
	description: z.string().trim().max(512).optional(),
	email: z.email().max(128).optional(),
	websites: z.array(z.url()).max(2).optional(),
	vertical: z.string().optional(),
	/** A resumable-upload handle from the photo upload, when the photo changes. */
	photoHandle: z.string().optional(),
});

/** One number's page: the summary plus what its tabs read. */
export const numberDetailSchema = numberSummarySchema.extend({
	label: z.string().nullable(),
	throughput: z.string().nullable(),
	officialBusiness: z.string().nullable(),
	platformType: z.string().nullable(),
	sipStatus: z.string().nullable(),
	/** The name shoppers see now; `nameStatus` is its review. */
	verifiedName: z.string().nullable(),
	/** The name last asked for and Meta's decision on it, until the number registers again. */
	requestedName: z
		.object({ name: z.string().nullable(), status: z.string() })
		.nullable(),
	/** Whether Hephon holds the number's two-step PIN, so it can register it again unasked. */
	registerPinSaved: z.boolean(),
	profile: businessProfileSchema.nullable(),
	automation: automationSchema,
	calling: callingConfigSchema.nullable(),
	commerce: z.object({
		cartEnabled: z.boolean(),
		catalogVisible: z.boolean(),
	}),
	testRecipients: z.array(z.string()),
	entryLinks: z.array(entryLinkSchema),
	devOverride: z
		.object({ host: z.string(), setAt: z.string(), by: z.string() })
		.nullable(),
	/** The setup checklist ("Products checked for WhatsApp" included). */
	checklist: z.array(
		z.object({
			key: z.string(),
			label: z.string(),
			done: z.boolean(),
			detail: z.string().nullable(),
		}),
	),
});
export type NumberDetail = z.infer<typeof numberDetailSchema>;

export const connectManualInput = organizationInput.extend({
	phoneNumberId: whatsappConnectionSchema.shape.phoneNumberId,
	wabaId: whatsappConnectionSchema.shape.wabaId,
	accessToken: accessTokenSchema,
	label: z.string().trim().max(60).optional(),
	storeScope: z.array(z.string().min(1)).default([]),
	defaultStoreExternalId: z.string().trim().min(1).nullable().default(null),
});

export const numberUpdateInput = numberInput.extend({
	label: z.string().trim().max(60).nullable().optional(),
	storeScope: z.array(z.string().min(1)).optional(),
	defaultStoreExternalId: z.string().trim().min(1).nullable().optional(),
	/** Refused while the catalog's content check is incomplete (spec 3.19). */
	enabled: z.boolean().optional(),
	testRecipients: z.array(testRecipientSchema).max(10).optional(),
	/** Write-only: a new token for the number's Messaging account. */
	accessToken: accessTokenSchema.optional(),
});

/** What the admin needs to open Embedded Signup v5 (never a secret). */
export const signupConfigSchema = z.object({
	appId: z.string().nullable(),
	configId: z.string().nullable(),
	graphVersion: z.string(),
	ready: z.boolean(),
});

/** Embedded Signup's session info, v5 (C6). */
export const signupSessionSchema = z.object({
	phoneNumberId: z.string().regex(/^\d{6,20}$/),
	messagingAccountIds: z.array(z.string()).default([]),
	primaryWaacIds: z.array(z.string()).default([]),
	catalogIds: z.array(z.string()).default([]),
	isOnBizApp: z.boolean().default(false),
	businessId: z.string().optional(),
});

export const completeSignupInput = organizationInput.extend({
	/** The code from FB.login; exchanged within 30 s. */
	code: z.string().min(1).max(2048),
	session: signupSessionSchema,
	label: z.string().trim().max(60).optional(),
});

export const signupEventInput = organizationInput.extend({
	event: z.enum(["FINISH", "CANCEL", "ERROR"]),
	currentStep: z.string().max(100).optional(),
	errorMessage: z.string().max(1000).optional(),
	errorId: z.string().max(100).optional(),
	sessionId: z.string().max(200).optional(),
});

export const costCategorySchema = z.object({
	category: z.string(),
	messages: z.number().int(),
	costUsd: z.number(),
});

/** Estimated Meta cost (D7: Meta bills the retailer; this never counts toward the cap). */
export const costsSchema = z.object({
	month: z.string(),
	estimated: z.literal(true),
	totalUsd: z.number(),
	byCategory: z.array(costCategorySchema),
	freeServiceUsed: z.number().int(),
	freeServiceLimit: z.number().int(),
	perConversationUsd: z.number().nullable(),
	perOrderUsd: z.number().nullable(),
	/** Meta's own figure once the daily reconciliation ran. */
	reconciledUsd: z.number().nullable(),
});

export const alertViewSchema = z.object({
	id: z.string(),
	code: z.string(),
	severity: z.enum(["CRITICAL", "WARNING", "INFO"]),
	title: z.string(),
	detail: z.unknown(),
	connectionId: z.string().nullable(),
	wabaId: z.string().nullable(),
	logId: z.string().nullable(),
	openedAt: z.date(),
	resolvedAt: z.date().nullable(),
});

export const contentCheckStatusSchema = z.object({
	judged: z.number().int(),
	total: z.number().int(),
	complete: z.boolean(),
	/** From the last run's cost per product; shown before an admin starts a run. */
	estimateUsd: z.number().nullable(),
	runningRunId: z.string().nullable(),
});

export const entryLinkViewSchema = entryLinkSchema.and(
	z.object({ url: z.url() }),
);

/** One WhatsApp message as the preview and the inbox draw it. */
export const outboundPreviewSchema = z.object({
	kind: z.string(),
	view: z.string().nullable(),
	payload: z.unknown(),
	/** "1 message · Multi-product list, 2 sections, 6 items". */
	summary: z.string(),
});

export const previewSourceSchema = z.union([
	z.object({ replyId: z.string().min(1) }),
	z.object({
		sample: z.enum([
			"search",
			"list",
			"single",
			"recipe",
			"basket",
			"location",
			"branch",
			"contact",
			"handoff",
			"checkout",
			"order_update",
		]),
	}),
]);

export const templateViewSchema = z.object({
	id: z.string(),
	whatsappAccountId: z.string(),
	metaTemplateId: z.string().nullable(),
	name: z.string(),
	language: z.string(),
	category: z.string(),
	purpose: z.string(),
	libraryTemplateName: z.string().nullable(),
	components: z.unknown(),
	status: z.string(),
	qualityScore: z.string().nullable(),
	rejection: z
		.object({ reason: z.string(), recommendation: z.string().nullable() })
		.nullable(),
	pausedUntil: z.date().nullable(),
	updatedAt: z.date(),
});

export const checkoutFlowStatusSchema = z.object({
	configured: z.boolean(),
	flowId: z.string().nullable(),
	status: z.string().nullable(),
	publishedAt: z.string().nullable(),
	/** Meta's signature status of the number's Flow public key at the last setup. */
	keySignature: z.string().nullable(),
	endpointUrl: z.string().nullable(),
	health: z.object({
		p90Ms: z.number().nullable(),
		errorRate: z.number().nullable(),
		alert: z.string().nullable(),
	}),
	/** Meta's preview link of the published Flow. */
	previewUrl: z.string().nullable(),
});

export const callingViewSchema = z.object({
	calling: callingConfigSchema,
	prerequisites: z.array(
		z.object({ key: z.string(), label: z.string(), done: z.boolean() }),
	),
	/** Changed settings can take this long to reach shoppers' phones. */
	clientRefreshDays: z.number().int(),
});
